<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Добавление и&nbsp;редактирование заказа</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-popast"
            class="typography__headline2"
          >
            Как попасть
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>Давайте добавим заказ в&nbsp;ваш проект.</p>

            <p>
              В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Заказы&raquo;. Если у&nbsp;вас еще нет
              заказов&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Добавить заказ&raquo;.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>Если заказ уже есть&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Добавить заказ&raquo;.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="dobavlenie-zakaza"
            class="typography__headline2"
          >
            Добавление заказа
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              В&nbsp;основной информации выберите
              <BaseLink
                class="ui-link-secondary"
                to="/faq/articles/sposoby-dostavki"
              >
                способ доставки</BaseLink
              >
              заказа.
            </p>

            <p>Стоимость доставки и&nbsp;трек-номер можно добавить позже, если эти данные еще неизвестны.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>Далее выберите клиента и&nbsp;его адрес для доставки заказа.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-6.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>В&nbsp;последнем блоке выберите товар нажав &laquo;Выбрать товары&raquo;.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-7.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>В&nbsp;появившемся окне выберите нужные товары и&nbsp;нажмите &laquo;Готово&raquo;.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-8.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>
              После выбора товаров, вы&nbsp;увидите их&nbsp;на&nbsp;странице. Можете изменить количество штук товара или
              удалить его из&nbsp;заказа.
            </p>

            <p>
              Как заполните все нужные данные заказа, нажмите &laquo;Добавить&raquo; в&nbsp;самом низу страницы. После
              успешного добавления увидите в&nbsp;правом верхнем углу уведомление.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-9.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="redaktirovanie-zakaza"
            class="typography__headline2"
          >
            Редактирование заказа
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              После добавления заказа, вы&nbsp;сможете найти его в&nbsp;<BaseLink
                class="ui-link-secondary"
                to="/faq/articles/spisok-zakazov-i-ikh-dannye"
              >
                списке заказов</BaseLink
              >.
            </p>

            <p>Отсюда можно быстро перейти к&nbsp;редактированию по&nbsp;иконке карандаша справа.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavlenie-i-redaktirovanie-zakaza-10.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С&nbsp;добавлением заказа разобрались. Самое время перейти к&nbsp;изучению
            <BaseLink
              class="ui-link-secondary"
              to="/faq/articles/spisok-zakazov-i-ikh-dannye"
              >списка заказов и&nbsp;просмотра их&nbsp;данных</BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Заказы с товарами",
    url: "/faq/categories/zakazy-s-tovarami",
  },
  {
    name: "Добавление и&nbsp;редактирование заказа",
  },
];

const menu = [
  {
    name: "Как&nbsp;попасть",
    url: "kak-popast",
  },
  {
    name: "Добавление&nbsp;заказа",
    url: "dobavlenie-zakaza",
  },
  {
    name: "Редактирование&nbsp;заказа",
    url: "redaktirovanie-zakaza",
  },
];

const relatedArticles = [
  {
    name: "Способы доставки",
    slug: "sposoby-dostavki",
  },
  {
    name: "Категории товаров",
    slug: "kategorii-tovarov",
  },
  {
    name: "Добавление и&nbsp;редактирование товара",
    slug: "dobavlenie-i-redaktirovanie-tovara",
  },
  {
    name: "Список товаров и&nbsp;их данные",
    slug: "spisok-tovarov-i-ikh-dannye",
  },
  {
    name: "Цифровые товары",
    slug: "tsifrovye-tovary",
  },
  {
    name: "Список заказов и&nbsp;их данные",
    slug: "spisok-zakazov-i-ikh-dannye",
  },
];

useSeoMeta({
  title: "Добавление и редактирование заказа",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
